import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useTranslation } from "../i18n/LanguageContext";

export default function Header() {
  const { t, lang, switchLang } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#about", label: t.header.navOurPractice },
    { href: "#pricing", label: t.header.navServices },
    { href: "#network", label: t.header.navPartners },
    { href: "#process", label: t.header.navProcess },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-[#0A0A0B]/90 backdrop-blur-md border-b border-[#2A2A2E]/50 py-4" : "py-6"
      }`}
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between gap-4">
        {/* Logo */}
        <a href="#" className="flex items-center" data-testid="logo">
          <img
            src="/fwdco-logo.png"
            alt="FWDCO Group"
            className={`transition-all duration-500 ${scrolled ? "h-14" : "h-16"} w-auto`}
            style={{ filter: "invert(1) hue-rotate(180deg)" }}
            draggable="false"
          />
        </a>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-10" data-testid="desktop-nav">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="text-[17px] text-[#B8B8C0] hover:text-[#F5F1E8] transition-colors duration-300"
              style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 400 }}
            >
              {link.label}
            </a>
          ))}
        </nav>

        {/* Right cluster: Language toggle + CTA */}
        <div className="flex items-center gap-3">
          {/* Language toggle */}
          <div
            className="hidden sm:flex items-center rounded-sm border border-[#2A2A2E] bg-[#0A0A0B]/40 overflow-hidden"
            data-testid="lang-switcher"
            style={{ fontFamily: "Montserrat, sans-serif" }}
          >
            <button
              type="button"
              onClick={() => lang !== "en" && switchLang("en")}
              className={`px-3 py-1.5 text-[13px] tracking-[0.22em] transition-colors ${
                lang === "en"
                  ? "bg-[#C9A96E] text-[#0A0A0B]"
                  : "text-[#B8B8C0] hover:text-[#F5F1E8]"
              }`}
              style={{ fontWeight: 600 }}
              data-testid="lang-en"
            >
              EN
            </button>
            <button
              type="button"
              onClick={() => lang !== "es" && switchLang("es")}
              className={`px-3 py-1.5 text-[13px] tracking-[0.22em] transition-colors ${
                lang === "es"
                  ? "bg-[#C9A96E] text-[#0A0A0B]"
                  : "text-[#B8B8C0] hover:text-[#F5F1E8]"
              }`}
              style={{ fontWeight: 600 }}
              data-testid="lang-es"
            >
              ES
            </button>
          </div>

          {/* CTA Button */}
          <a
            href="#contact"
            className="hidden md:inline-flex h-11 px-6 bg-[#C9A96E] text-[#0A0A0B] text-[16px] items-center justify-center hover:bg-[#E2C98A] transition-all duration-300"
            style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 600, borderRadius: "2px" }}
            data-testid="header-cta"
          >
            {t.header.cta}
          </a>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-[#F5F1E8]"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-[#141417] border-t border-[#2A2A2E] mt-4"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col p-6 gap-4">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="text-[17px] text-[#B8B8C0] hover:text-[#F5F1E8] transition-colors"
                  style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 400 }}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </a>
              ))}

              <div className="flex items-center rounded-sm border border-[#2A2A2E] bg-[#0A0A0B]/40 overflow-hidden self-start mt-2">
                <button
                  type="button"
                  onClick={() => { if (lang !== "en") switchLang("en"); setMobileMenuOpen(false); }}
                  className={`px-4 py-2 text-[13px] tracking-[0.22em] ${lang === "en" ? "bg-[#C9A96E] text-[#0A0A0B]" : "text-[#B8B8C0]"}`}
                  style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 600 }}
                >
                  EN
                </button>
                <button
                  type="button"
                  onClick={() => { if (lang !== "es") switchLang("es"); setMobileMenuOpen(false); }}
                  className={`px-4 py-2 text-[13px] tracking-[0.22em] ${lang === "es" ? "bg-[#C9A96E] text-[#0A0A0B]" : "text-[#B8B8C0]"}`}
                  style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 600 }}
                >
                  ES
                </button>
              </div>

              <a
                href="#contact"
                className="mt-4 h-12 px-6 bg-[#C9A96E] text-[#0A0A0B] inline-flex items-center justify-center"
                style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 600, borderRadius: "2px" }}
                onClick={() => setMobileMenuOpen(false)}
              >
                {t.header.cta}
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
